import React, { useState } from "react";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import {
    FileImageOutlined,
    LeftOutlined,
    RightOutlined,
    ReloadOutlined,
    DoubleLeftOutlined,
    CopyOutlined,
} from "@ant-design/icons";
import log from "cslog";
import styles from "./styles/ContentCalender.module.css";
import { PreviewTemplateList } from "../qmade/creator/PreviewTemplateList";
import { getPublicImageURL } from "../_helpers/image";
import { Button, Card, Tabs, Spin, message, Grid, Divider } from "antd";
import { useItemsFine, useCreateItemMutation } from "../_actions/app/items";
import { useGenerateBio } from "../_actions/bot";

const ASSETS_GROUPS = [
    {
        id: "facebook",
        title: "Facebook",
        icon: "fab fa-facebook",
        color: "#1877f2",
        assets: [
            {
                id: "facebook_profile",
                title: "Facebook Profile Picture",
                type: "template",
                filter: "filter[used_schemas][qdb_creation_schema_id][id][_eq]=6a9e2473-24f1-4ff2-b813-71f634c1fd2e",
            },
            {
                id: "facebook_cover",
                title: "Facebook Cover",
                type: "template",
                filter: "filter[used_schemas][qdb_creation_schema_id][id][_eq]=9ce5f30d-c58a-4220-86a0-5ecd47aeb657&filter[types][qdb_creation_type_id][_eq]=25",
                height: "70px",
            },
            {
                id: "facebook_about",
                title: "Facebook About",
                type: "text",
            },
        ],
    },
    {
        id: "linkedin",
        title: "LinkedIn",
        icon: "fab fa-linkedin",
        color: "#0a66c2",
        assets: [
            {
                id: "linkedin_profile",
                title: "LinkedIn Profile Picture",
                type: "template",
                filter: "filter[used_schemas][qdb_creation_schema_id][id][_eq]=6a9e2473-24f1-4ff2-b813-71f634c1fd2e",
            },
            {
                id: "linkedin_cover",
                title: "LinkedIn Cover",
                type: "template",
                filter: "filter[used_schemas][qdb_creation_schema_id][id][_eq]=ed42abb4-03a1-4b1b-9580-969086ccd502&filter[types][qdb_creation_type_id][_eq]=51",
                height: "70px",
            },
            {
                id: "linkedin_about",
                title: "LinkedIn About",
                type: "text",
            },
        ],
    },
    {
        id: "instagram",
        title: "Instagram",
        icon: "fab fa-instagram",
        color: "#e1306c",
        assets: [
            {
                id: "instagram_profile",
                title: "Instagram Profile Picture",
                type: "template",
                filter: "filter[used_schemas][qdb_creation_schema_id][id][_eq]=6a9e2473-24f1-4ff2-b813-71f634c1fd2e",
            },
            {
                id: "instagram_bio",
                title: "Instagram Bio",
                type: "text",
            },
        ],
    },
    {
        id: "pinterest",
        title: "Pinterest",
        icon: "fab fa-pinterest",
        color: "#e60023",
        assets: [
            {
                id: "pinterest_profile",
                title: "Pinterest Profile Picture",
                type: "template",
                filter: "filter[used_schemas][qdb_creation_schema_id][id][_eq]=6a9e2473-24f1-4ff2-b813-71f634c1fd2e",
            },
        ],
    },
];

function BrandAssets({ me = {} }) {
    const [open, setOpen] = useState(false);
    const [selected_group, setSelectedGroup] = useState(ASSETS_GROUPS[0]);
    const [selected_card, setSelectedCard] = useState(null);
    const [platformBios, setPlatformBios] = useState({});

    return (
        <QurealSection
            header={
                <SectionHeader
                    heading="Brand Assets"
                    icon={<FileImageOutlined />}
                />
            }
        >
            <div className={styles.wrapper}>
                <Tabs
                    defaultActiveKey="facebook"
                    onChange={(key) => {
                        setSelectedGroup(
                            ASSETS_GROUPS.find((group) => group.id === key)
                        );
                    }}
                    items={ASSETS_GROUPS.map((group) => ({
                        key: group.id,
                        label: group.title,
                        icon: (
                            <i
                                className={group.icon}
                                style={{ color: group.color }}
                            />
                        ),
                    }))}
                />

                {selected_group?.assets.map((item) => (
                    <AssetPage
                        key={item.id}
                        asset={item}
                        onCardClick={(card) => {
                            log.d(card, "Card clicked");
                            setOpen(true);
                            setSelectedCard(card);
                        }}
                        currentPlatform={selected_group.id}
                        platformBios={platformBios}
                        setPlatformBios={setPlatformBios}
                        me={me}
                    />
                ))}
            </div>

            {selected_card && (
                <PreviewTemplateList
                    enable={open}
                    filter={selected_card?.filter || null}
                    open={open}
                    setOpen={setOpen}
                    me={me}
                />
            )}
        </QurealSection>
    );
}

function AssetPage({
    asset,
    onCardClick,
    currentPlatform,
    platformBios,
    setPlatformBios,
    me,
}) {
    const isCover = asset?.id?.includes("cover");

    return (
        <div className={styles.section_block}>
            <Divider
                orientation="left"
                orientationMargin={0}
                style={{ marginTop: 4, marginBottom: 16 }}
            >
                {asset.title}
            </Divider>

            {asset.type === "template" && (
                <AssetRow
                    asset={asset}
                    onCardClick={onCardClick}
                    isCover={isCover}
                    showMoreEnabled={isCover}
                />
            )}
            {asset.type === "text" && (
                <AboutRow
                    platform={currentPlatform}
                    platformBios={platformBios}
                    setPlatformBios={setPlatformBios}
                    assetId={asset.id}
                    me={me}
                />
            )}
        </div>
    );
}

export default BrandAssets;

function AssetRow({
    asset,
    onCardClick,
    isCover = false,
    showMoreEnabled = false,
}) {
    const { isSuccess, isLoading, data } = useItemsFine(
        "qdb_creation_template",
        asset.filter,
        true
    );
    const [showAll, setShowAll] = useState(false);
    const items = isSuccess ? data : [];
    const visibleItems =
        showMoreEnabled && !showAll ? items.slice(0, 3) : items;
    const skeletonCount = isCover ? 2 : 4;

    const gridClass = isCover ? styles.cards_grid_wide : styles.cards_grid;
    const skeletonClass = isCover
        ? `${styles.card_skeleton} ${styles.card_skeleton_wide}`
        : styles.card_skeleton;

    return (
        <div className={gridClass}>
            {isLoading &&
                Array.from({ length: skeletonCount }).map((_, i) => (
                    <div key={i} className={skeletonClass} />
                ))}

            {isSuccess &&
                visibleItems.map((item) => (
                    <OneCard
                        key={item.id}
                        item={{
                            feature_image: item?.preview,
                            name: item?.title,
                        }}
                        isCover={isCover}
                        onCardClick={() =>
                            onCardClick({
                                filter: JSON.stringify({
                                    id: { _eq: item.id },
                                }),
                                template: item,
                            })
                        }
                    />
                ))}

            {showMoreEnabled && items.length > 3 && (
                <div className={styles.show_more_wrap}>
                    {!showAll ? (
                        <Button
                            type="link"
                            icon={<DoubleLeftOutlined rotate={270} />}
                            iconPosition="end"
                            onClick={() => setShowAll(true)}
                        >
                            Show more
                        </Button>
                    ) : (
                        <Button
                            type="link"
                            icon={<DoubleLeftOutlined rotate={90} />}
                            iconPosition="end"
                            onClick={() => setShowAll(false)}
                        >
                            Show less
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

function AboutRow({ platform, platformBios, setPlatformBios, assetId, me }) {
    const [index, setIndex] = useState(0);

    const { data: cachedData, isLoading: isCacheLoading } = useItemsFine(
        "qdb_ai_cache",
        `filter[function][_eq]=${assetId}`,
        true
    );
    const createCacheMutation = useCreateItemMutation();
    const mutationBio = useGenerateBio((result) => {
        setPlatformBios((prev) => ({ ...prev, [platform]: result }));
        if (result && Array.isArray(result)) {
            result.forEach((bioItem) => {
                const content =
                    typeof bioItem === "string" ? bioItem : bioItem?.bio;
                if (content) {
                    createCacheMutation.mutate({
                        collection: "qdb_ai_cache",
                        data: {
                            function: assetId,
                            organization: me?.selected_org?.id,
                            data: content,
                        },
                    });
                }
            });
        }
    });

    React.useEffect(() => {
        if (cachedData && cachedData.length > 0 && !platformBios[platform]) {
            setPlatformBios((prev) => ({
                ...prev,
                [platform]: cachedData.map((item) => item.data),
            }));
            setIndex(0);
        } else if (
            !platformBios[platform] &&
            !isCacheLoading &&
            cachedData !== undefined
        ) {
            setIndex(0);
            mutationBio.mutate({ platform });
        } else if (platformBios[platform]) {
            setIndex(0);
        }
    }, [platform, cachedData, isCacheLoading, assetId]);

    const currentBiosData = platformBios[platform] || [];
    const bios = Array.isArray(currentBiosData)
        ? currentBiosData
              .map((it) => (typeof it === "string" ? it : it?.bio))
              .filter(Boolean)
        : [];

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const handleRegenerate = React.useCallback(() => {
        setIndex(0);
        mutationBio.mutate({ platform });
    }, [platform]);

    const handleCopy = React.useCallback(async () => {
        const text = bios[index] || "";
        if (!text) {
            message.warning("Nothing to copy");
            return;
        }
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement("textarea");
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            message.success("Copied to clipboard");
        } catch (err) {
            message.error("Failed to copy");
        }
    }, [bios, index]);

    const isLoading = mutationBio.isLoading || isCacheLoading;

    return (
        <div>
            {isLoading && (
                <div className={styles.loading_row}>
                    <Spin size="small" />
                    <span>Generating suggestions</span>
                </div>
            )}

            {!isLoading && bios.length > 0 && (
                <Card
                    className={styles.bio_card}
                    style={{
                        maxWidth: 600,
                        // borderLeft: "3px solid #00998E",
                    }}
                    bodyStyle={{ padding: 0 }}
                >
                    <p className={styles.bio_text} key={index}>
                        {bios[index]}
                    </p>
                    <div className={styles.bio_footer}>
                        <div className={styles.bio_footer_left}>
                            <Button
                                icon={<CopyOutlined />}
                                onClick={handleCopy}
                                disabled={
                                    mutationBio.isLoading || bios.length === 0
                                }
                            >
                                Copy
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRegenerate}
                                disabled={mutationBio.isLoading}
                            >
                                {!isMobile && "Regenrate"}
                            </Button>
                        </div>
                        <div className={styles.bio_footer_right}>
                            <Button
                                icon={<LeftOutlined />}
                                onClick={() =>
                                    setIndex((i) => Math.max(0, i - 1))
                                }
                                disabled={index === 0}
                            >
                                {!isMobile && "Previous"}
                            </Button>
                            <span>
                                {index + 1}/{bios.length}
                            </span>
                            <Button
                                // type="primary"
                                icon={<RightOutlined />}
                                iconPosition="end"
                                onClick={() =>
                                    setIndex((i) =>
                                        Math.min(bios.length - 1, i + 1)
                                    )
                                }
                                disabled={index >= bios.length - 1}
                            >
                                {!isMobile && "Next"}
                            </Button>
                        </div>
                    </div>
                    <div className={styles.bio_count_row}></div>
                </Card>
            )}

            {!isLoading && mutationBio.isSuccess && bios.length === 0 && (
                <p className={styles.empty_state}>No suggestions generated.</p>
            )}
        </div>
    );
}

function OneCard({ item, onCardClick, isCover = false }) {
    return (
        <div className={styles.card} onClick={onCardClick}>
            <div
                className={`${styles.card_img_wrap} ${
                    isCover ? styles.card_img_wrap_wide : ""
                }`}
            >
                <img
                    src={getPublicImageURL(item.feature_image)}
                    alt={item.name || ""}
                    className={styles.card_img}
                />
                <div className={styles.card_overlay}>
                    <span className={styles.card_overlay_text}>Use this</span>
                </div>
            </div>
        </div>
    );
}
